import { toStateCode } from "@/components/company-census/data";
import type { BusinessInfo, Sex, TeamMember, WaiveReason } from "@/components/company-census/types";

/**
 * Wire contract + mappers for the company-census DRAFT (the single source of
 * truth in db-service, reached through the authenticated platform-API proxy at
 * `/api/quote-census/draft`). The platform API derives the caller's identity
 * from the Cognito session, so the browser never sends tenant/email/org.
 *
 * Shapes mirror `apps/api`'s `CensusDraftResponseSchema` /
 * `CensusDraftWriteRequestSchema` (camelCase at the HTTP boundary).
 */

export interface CensusDraftDependentWire {
  dependentId: string;
  relationship: "spouse" | "child";
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  sex: "male" | "female" | null;
  zip: string | null;
}

export interface CensusDraftMemberWire {
  memberId: string;
  firstName: string;
  lastName: string;
  sex: "male" | "female" | null;
  zip: string | null;
  dateOfBirth: string | null;
  dependentMethod: "manual" | "none";
  enrolling: boolean;
  notEnrollingReason: WaiveReason | null;
  dependents: CensusDraftDependentWire[];
}

export interface CensusDraftWire {
  submissionId: string;
  status: "draft" | "submitted";
  business: {
    name: string;
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    phone: string | null;
    ein: string | null;
  };
  members: CensusDraftMemberWire[];
}

export interface CensusDraftWriteDependent {
  relationship: "spouse" | "child";
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  sex: "male" | "female" | null;
  zip: string | null;
}

export interface CensusDraftWriteMember {
  firstName: string;
  lastName: string;
  sex: "male" | "female" | null;
  zip: string | null;
  dateOfBirth: string | null;
  dependentMethod: "manual" | "none";
  enrolling: boolean;
  notEnrollingReason: WaiveReason | null;
  dependents: CensusDraftWriteDependent[];
}

export interface CensusDraftWriteRequest {
  business: {
    name: string;
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    phone: string | null;
    ein: string | null;
  };
  members: CensusDraftWriteMember[];
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Map a draft aggregate's business profile into the form's business state. */
export function draftToBusiness(draft: CensusDraftWire): BusinessInfo {
  const b = draft.business;
  return {
    name: b.name ?? "",
    ein: b.ein ?? "",
    address: b.address ?? "",
    city: b.city ?? "",
    // Normalize to the USPS code the state select keys on; keep the raw value if
    // it isn't a recognizable state so nothing is silently dropped.
    state: toStateCode(b.state) || (b.state ?? ""),
    zip: b.zip ?? "",
    phone: b.phone ?? "",
  };
}

/** Map a draft aggregate's roster into the form's team-member state. */
export function draftToMembers(draft: CensusDraftWire): TeamMember[] {
  return draft.members.map((m) => ({
    id: m.memberId,
    firstName: m.firstName ?? "",
    lastName: m.lastName ?? "",
    sex: (m.sex ?? "") as Sex | "",
    zip: m.zip ?? "",
    dateOfBirth: m.dateOfBirth ?? "",
    dependentMethod: m.dependentMethod ?? null,
    dependents: m.dependents.map((d) => ({
      id: d.dependentId,
      relationship: d.relationship,
      firstName: d.firstName ?? "",
      lastName: d.lastName ?? "",
      dateOfBirth: d.dateOfBirth ?? "",
      sex: (d.sex ?? "") as Sex | "",
      zip: d.zip ?? "",
    })),
    enrolling: m.enrolling,
    notEnrollingReason: m.notEnrollingReason ?? undefined,
    dependentsSameZip: true,
  }));
}

/**
 * Build the draft write payload from the in-memory flow state. Members without
 * BOTH a first and last name are dropped — db-service requires both, and a
 * half-typed row shouldn't fail (or pollute) the autosave. The local UI keeps
 * the row; it just isn't persisted until complete.
 */
export function buildCensusDraftWrite(
  business: BusinessInfo,
  members: TeamMember[],
): CensusDraftWriteRequest {
  const mappedMembers: CensusDraftWriteMember[] = members
    .filter((m) => m.firstName.trim().length > 0 && m.lastName.trim().length > 0)
    .map((m) => {
      const enrolling = m.enrolling !== false;
      // Default on: dependents inherit the member's ZIP unless toggled off.
      const sameZip = m.dependentsSameZip !== false;
      const memberZip = emptyToNull(m.zip);
      return {
        firstName: m.firstName.trim(),
        lastName: m.lastName.trim(),
        sex: m.sex === "" ? null : m.sex,
        zip: memberZip,
        dateOfBirth: emptyToNull(m.dateOfBirth),
        dependentMethod: m.dependents.length > 0 ? "manual" : "none",
        enrolling,
        notEnrollingReason: enrolling ? null : (m.notEnrollingReason ?? null),
        dependents: enrolling
          ? m.dependents.map((d) => ({
              relationship: d.relationship,
              firstName: emptyToNull(d.firstName),
              lastName: emptyToNull(d.lastName),
              dateOfBirth: emptyToNull(d.dateOfBirth),
              sex: d.sex === "" ? null : d.sex,
              zip: sameZip ? memberZip : emptyToNull(d.zip),
            }))
          : [],
      };
    });

  return {
    business: {
      name: business.name.trim(),
      address: emptyToNull(business.address),
      city: emptyToNull(business.city),
      state: emptyToNull(business.state),
      zip: emptyToNull(business.zip),
      phone: emptyToNull(business.phone),
      ein: emptyToNull(business.ein),
    },
    members: mappedMembers,
  };
}
